// src/pages/Settings.js
import React, { useState, useEffect, createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Settings as SettingsIcon, 
  Trash2,
  Save,
  AlertTriangle,
  Eye,
  EyeOff
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

// Dark Mode Context
const DarkModeContext = createContext();

export const useDarkMode = () => {
  const context = useContext(DarkModeContext);
  if (!context) {
    throw new Error('useDarkMode must be used within a DarkModeProvider');
  }
  return context;
};

export const DarkModeProvider = ({ children }) => {
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  const toggleDarkMode = () => {
    setDarkMode(prev => !prev);
  };

  return (
    <DarkModeContext.Provider value={{ darkMode, toggleDarkMode }}>
      {children}
    </DarkModeContext.Provider>
  );
};

const Settings = () => {
  const { darkMode, toggleDarkMode } = useDarkMode();
  const { logout } = useAuth();
  const navigate = useNavigate();
  
  const [settings, setSettings] = useState({
    darkMode: darkMode,
    language: 'en',
    timezone: 'America/New_York',
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12h'
  });

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [showDeletePassword, setShowDeletePassword] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    setSettings(prev => ({
      ...prev,
      darkMode: darkMode
    }));
  }, [darkMode]);

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
    
    if (key === 'darkMode') {
      toggleDarkMode();
    }
  };

  const handleSaveSettings = () => {
    console.log('Saving settings:', settings);
    toast.success('Settings saved successfully!');
  };


  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      setDeleteError('Please enter your password to confirm.');
      return;
    }

    setDeleteError('');
    setIsDeleting(true);

    try {
      const token = localStorage.getItem('authToken');
      await axios.delete('/api/user/account', {
        headers: { Authorization: `Bearer ${token}` },
        data: { password: deletePassword }
      });

      toast.success('Your account has been deactivated.');
      logout();
      navigate('/login');
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to deactivate account. Please try again.';
      setDeleteError(message);
      toast.error(message);
    } finally {
      setIsDeleting(false);
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteConfirm(false);
    setDeletePassword('');
    setDeleteError('');
    setShowDeletePassword(false);
  };

  const SettingSection = ({ title, description, children }) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700/50 p-6 transition-colors duration-200">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
        {description && <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{description}</p>}
      </div>
      {children}
    </div>
  );

  const ToggleSetting = ({ label, description, checked, onChange }) => (
    <div className="flex items-center justify-between">
      <div className="flex-1">
        <label className="text-sm font-medium text-gray-900 dark:text-gray-100">{label}</label>
        {description && <p className="text-sm text-gray-500 dark:text-gray-400">{description}</p>}
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${
          checked ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
            checked ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );


  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 transition-colors duration-200">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 flex items-center">
            <SettingsIcon className="mr-3 h-8 w-8 text-blue-600 dark:text-blue-400" />
            Settings
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">Manage your account preferences and application settings</p>
        </div>

        <div className="space-y-6">
          <SettingSection
            title="Appearance & Display"
            description="Customize how the application looks and feels"
          >
            <div className="space-y-4">
              <ToggleSetting
                label="Dark Mode"
                description="Use dark theme across the application"
                checked={settings.darkMode}
                onChange={(value) => handleSettingChange('darkMode', value)}
              />
            </div>
          </SettingSection>

          <SettingSection
            title="Account Actions"
            description="Manage your account"
          >
            <div className="space-y-4">

              <div className="flex items-center justify-between p-4 bg-red-50 dark:bg-red-900/30 rounded-lg transition-colors duration-200">
                <div className="flex items-center">
                  <Trash2 className="h-5 w-5 text-red-600 dark:text-red-400 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Deactivate Account</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Deactivate your account and sign out. Your data is retained.</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="px-4 py-2 bg-red-600 dark:bg-red-500 text-white rounded-md text-sm font-medium hover:bg-red-700 dark:hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 transition-colors duration-200"
                >
                  Deactivate Account
                </button>
              </div>
            </div>
          </SettingSection>

          <div className="flex justify-end">
            <button
              onClick={handleSaveSettings}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-blue-400 dark:focus:ring-offset-gray-900 transition-colors duration-200"
            >
              <Save className="h-5 w-5 mr-2" />
              Save Settings
            </button>
          </div>
        </div>

        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black dark:bg-opacity-70 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-96 mx-4 transition-colors duration-200">
              <div className="flex items-center mb-4">
                <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Deactivate Account</h3>
              </div>
              
              <div className="mb-6">
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Are you sure you want to deactivate your account? You'll be signed out and won't be able to log back in until your account is reactivated.
                </p>
                <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg p-3 transition-colors duration-200">
                  <p className="text-sm text-red-800 dark:text-red-300">
                    <strong>Your data will be retained:</strong>
                  </p>
                  <ul className="text-sm text-red-700 dark:text-red-400 mt-2 list-disc list-inside">
                    <li>Flight logs and records are not deleted</li>
                    <li>Aircraft and crew information is not deleted</li>
                    <li>Account settings and preferences are not deleted</li>
                    <li>Contact support if you'd like your data permanently removed</li>
                  </ul>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Enter your password to confirm
                  </label>
                  <div className="relative">
                    <input
                      type={showDeletePassword ? 'text' : 'password'}
                      value={deletePassword}
                      onChange={(e) => {
                        setDeletePassword(e.target.value);
                        if (deleteError) setDeleteError('');
                      }}
                      autoFocus
                      className={`block w-full px-3 py-2 pr-10 border rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors duration-200 ${
                        deleteError ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      placeholder="Your password"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowDeletePassword(prev => !prev)}
                    >
                      {showDeletePassword ? (
                        <EyeOff className="h-5 w-5 text-gray-400" />
                      ) : (
                        <Eye className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                  {deleteError && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{deleteError}</p>
                  )}
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={closeDeleteModal}
                  disabled={isDeleting}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={isDeleting}
                  className="px-4 py-2 bg-red-600 dark:bg-red-500 text-white rounded-md text-sm font-medium hover:bg-red-700 dark:hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 transition-colors duration-200 disabled:opacity-50"
                >
                  {isDeleting ? 'Deactivating...' : 'Deactivate Account'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;